import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  Menu,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Storefront as StorefrontIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { workflowService } from '../services/workflow.service';
import { apiService } from '../services/api';
import { notifyCredentialsChanged } from '../services/credentialEvents';
import { Workflow, WorkflowCredential } from '../types/workflow.types';

const CREDENTIAL_TYPES: WorkflowCredential['credential_type'][] = [
  'postgres',
  'smtp',
  'gmail',
  'odoo',
  'supabase',
  'imap',
  'http',
  'shopify',
  'hubspot',
  'slack',
  'pipedrive',
  'microsoft365',
  'asana',
  'trello',
  'netsuite',
  'quickbooks',
  'sap',
  'zoom',
  'jira',
  'confluence',
  'discord',
  'microsoft_teams',
  'google_calendar',
  'google_sheets',
  'google_drive',
  'zoho_crm',
  'insightly',
  'cal_com',
  'erpnext',
  'notion',
  'airtable',
  'dropbox',
  'mailchimp',
  'xero',
  'myob',
  // MQTT brokers — Node-RED imports auto-create one of these per
  // ``mqtt-broker`` config node so ``action.mqtt.publish`` nodes wire up
  // out of the box.
  'mqtt',
  'custom',
];

type FieldKind = 'text' | 'password' | 'number' | 'boolean' | 'select';
type FieldDef = {
  key: string;
  label: string;
  kind: FieldKind;
  options?: string[];
};

const FIELD_DEFS: Record<WorkflowCredential['credential_type'], FieldDef[]> = {
  postgres: [
    { key: 'host', label: 'Host', kind: 'text' },
    { key: 'port', label: 'Port', kind: 'number' },
    { key: 'database', label: 'Database', kind: 'text' },
    { key: 'user', label: 'User', kind: 'text' },
    { key: 'password', label: 'Password', kind: 'password' },
    { key: 'ssl_mode', label: 'SSL Mode', kind: 'select', options: ['disable', 'allow', 'prefer', 'require', 'verify-ca', 'verify-full'] },
  ],
  smtp: [
    { key: 'host', label: 'Host', kind: 'text' },
    { key: 'port', label: 'Port', kind: 'number' },
    { key: 'username', label: 'Username', kind: 'text' },
    { key: 'password', label: 'Password', kind: 'password' },
    { key: 'use_tls', label: 'Use TLS', kind: 'boolean' },
    { key: 'from_email', label: 'From Email', kind: 'text' },
  ],
  gmail: [
    { key: 'access_token', label: 'Access Token (manual; prefer "Connect with Google")', kind: 'password' },
    { key: 'refresh_token', label: 'Refresh Token (auto-filled by OAuth)', kind: 'password' },
    { key: 'email', label: 'Connected Email (auto-filled by OAuth)', kind: 'text' },
  ],
  odoo: [
    { key: 'base_url', label: 'Base URL', kind: 'text' },
    { key: 'db', label: 'Database', kind: 'text' },
    { key: 'username', label: 'Username (login email)', kind: 'text' },
    { key: 'password', label: 'Password', kind: 'password' },
  ],
  supabase: [
    { key: 'base_url', label: 'Base URL', kind: 'text' },
    { key: 'api_key', label: 'API Key', kind: 'password' },
  ],
  imap: [
    { key: 'host', label: 'Host', kind: 'text' },
    { key: 'port', label: 'Port', kind: 'number' },
    { key: 'username', label: 'Username', kind: 'text' },
    { key: 'password', label: 'Password', kind: 'password' },
    { key: 'use_ssl', label: 'Use SSL', kind: 'boolean' },
    { key: 'mailbox', label: 'Mailbox', kind: 'text' },
  ],
  http: [
    { key: 'base_url', label: 'Base URL', kind: 'text' },
    { key: 'auth_token', label: 'Auth Token', kind: 'password' },
    { key: 'api_key', label: 'API Key', kind: 'password' },
  ],
  shopify: [
    { key: 'shop_domain', label: 'Shop Domain (e.g. mystore.myshopify.com)', kind: 'text' },
    { key: 'access_token', label: 'Admin API Access Token', kind: 'password' },
    { key: 'api_version', label: 'API Version (e.g. 2024-01)', kind: 'text' },
  ],
  hubspot: [
    { key: 'access_token', label: 'Private App Token', kind: 'password' },
  ],
  slack: [
    { key: 'token', label: 'Bot OAuth Token (xoxb-...)', kind: 'password' },
  ],
  pipedrive: [
    { key: 'api_key', label: 'API Key', kind: 'password' },
  ],
  microsoft365: [
    { key: 'access_token', label: 'Access Token (OAuth)', kind: 'password' },
    { key: 'user_id', label: 'User ID or "me"', kind: 'text' },
  ],
  asana: [
    { key: 'access_token', label: 'Personal Access Token', kind: 'password' },
  ],
  trello: [
    { key: 'api_key', label: 'API Key', kind: 'text' },
    { key: 'token', label: 'Token', kind: 'password' },
  ],
  netsuite: [
    { key: 'account_id', label: 'Account ID', kind: 'text' },
    { key: 'consumer_key', label: 'Consumer Key', kind: 'text' },
    { key: 'consumer_secret', label: 'Consumer Secret', kind: 'password' },
    { key: 'token_id', label: 'Token ID', kind: 'text' },
    { key: 'token_secret', label: 'Token Secret', kind: 'password' },
  ],
  quickbooks: [
    { key: 'access_token', label: 'Access Token (OAuth)', kind: 'password' },
    { key: 'realm_id', label: 'Realm ID (Company ID)', kind: 'text' },
  ],
  sap: [
    { key: 'base_url', label: 'Base URL (e.g. https://my.sap.host)', kind: 'text' },
    { key: 'username', label: 'Username', kind: 'text' },
    { key: 'password', label: 'Password', kind: 'password' },
    { key: 'access_token', label: 'Access Token (optional, overrides basic auth)', kind: 'password' },
  ],
  zoom: [
    { key: 'account_id', label: 'Account ID', kind: 'text' },
    { key: 'client_id', label: 'Client ID', kind: 'text' },
    { key: 'client_secret', label: 'Client Secret', kind: 'password' },
  ],
  jira: [
    { key: 'base_url', label: 'Base URL (e.g. https://yoursite.atlassian.net)', kind: 'text' },
    { key: 'email', label: 'Email', kind: 'text' },
    { key: 'api_token', label: 'API Token', kind: 'password' },
  ],
  confluence: [
    { key: 'base_url', label: 'Base URL (e.g. https://yoursite.atlassian.net)', kind: 'text' },
    { key: 'email', label: 'Email', kind: 'text' },
    { key: 'api_token', label: 'API Token', kind: 'password' },
  ],
  discord: [
    { key: 'webhook_url', label: 'Incoming Webhook URL (optional)', kind: 'text' },
    { key: 'bot_token', label: 'Bot Token (optional, for bot operations)', kind: 'password' },
    { key: 'channel_id', label: 'Default Channel ID (for bot)', kind: 'text' },
  ],
  microsoft_teams: [
    { key: 'webhook_url', label: 'Incoming Webhook URL', kind: 'text' },
  ],
  google_calendar: [
    { key: 'access_token', label: 'Access Token (manual; prefer "Connect with Google")', kind: 'password' },
    { key: 'refresh_token', label: 'Refresh Token (auto-filled by OAuth)', kind: 'password' },
    { key: 'email', label: 'Connected Email (auto-filled by OAuth)', kind: 'text' },
  ],
  google_sheets: [
    { key: 'access_token', label: 'Access Token (manual; prefer "Connect with Google")', kind: 'password' },
    { key: 'refresh_token', label: 'Refresh Token (auto-filled by OAuth)', kind: 'password' },
    { key: 'email', label: 'Connected Email (auto-filled by OAuth)', kind: 'text' },
  ],
  google_drive: [
    { key: 'access_token', label: 'Access Token (manual; prefer "Connect with Google")', kind: 'password' },
    { key: 'refresh_token', label: 'Refresh Token (auto-filled by OAuth)', kind: 'password' },
    { key: 'email', label: 'Connected Email (auto-filled by OAuth)', kind: 'text' },
  ],
  zoho_crm: [
    { key: 'access_token', label: 'OAuth Access Token', kind: 'password' },
    { key: 'refresh_token', label: 'OAuth Refresh Token', kind: 'password' },
    { key: 'client_id', label: 'Client ID', kind: 'text' },
    { key: 'client_secret', label: 'Client Secret', kind: 'password' },
    { key: 'region', label: 'Region (com / eu / in / …)', kind: 'text' },
  ],
  insightly: [
    { key: 'api_key', label: 'API Key', kind: 'password' },
  ],
  cal_com: [
    { key: 'api_key', label: 'API Key', kind: 'password' },
    { key: 'base_url', label: 'Base URL (leave blank for Cal.com cloud)', kind: 'text' },
  ],
  erpnext: [
    { key: 'base_url', label: 'Base URL (e.g. https://myerp.frappe.cloud)', kind: 'text' },
    { key: 'api_key', label: 'API Key', kind: 'text' },
    { key: 'api_secret', label: 'API Secret', kind: 'password' },
  ],
  notion: [
    { key: 'api_token', label: 'Integration Token (secret_…)', kind: 'password' },
  ],
  airtable: [
    { key: 'access_token', label: 'Personal Access Token', kind: 'password' },
    { key: 'base_id', label: 'Default Base ID (appXXX…)', kind: 'text' },
  ],
  dropbox: [
    { key: 'access_token', label: 'OAuth Access Token', kind: 'password' },
  ],
  mailchimp: [
    { key: 'api_key', label: 'API Key', kind: 'password' },
    { key: 'server_prefix', label: 'Server Prefix (e.g. us21)', kind: 'text' },
  ],
  xero: [
    { key: 'access_token', label: 'OAuth Access Token', kind: 'password' },
    { key: 'tenant_id', label: 'Tenant ID (Xero Organisation UUID)', kind: 'text' },
    { key: 'refresh_token', label: 'Refresh Token (optional, enables auto-refresh)', kind: 'password' },
    { key: 'client_id', label: 'Client ID (optional, for refresh)', kind: 'text' },
    { key: 'client_secret', label: 'Client Secret (optional, for refresh)', kind: 'password' },
  ],
  myob: [
    { key: 'access_token', label: 'OAuth Access Token', kind: 'password' },
    { key: 'api_key', label: 'API Key (x-myobapi-key, developer key)', kind: 'password' },
    { key: 'company_file_id', label: 'Default Company File ID (optional)', kind: 'text' },
    { key: 'cftoken', label: 'Company File Token (base64 of user:password)', kind: 'password' },
    { key: 'refresh_token', label: 'Refresh Token (optional, enables auto-refresh)', kind: 'password' },
    { key: 'client_id', label: 'Client ID (optional, for refresh)', kind: 'text' },
    { key: 'client_secret', label: 'Client Secret (optional, for refresh)', kind: 'password' },
  ],
  mqtt: [
    { key: 'host', label: 'Broker Host (e.g. broker.hivemq.com)', kind: 'text' },
    { key: 'port', label: 'Broker Port (1883 plain, 8883 TLS)', kind: 'number' },
    { key: 'username', label: 'Username (optional)', kind: 'text' },
    { key: 'password', label: 'Password (optional)', kind: 'password' },
    { key: 'client_id', label: 'Client ID (optional — auto-generated if empty)', kind: 'text' },
    { key: 'use_tls', label: 'Use TLS', kind: 'boolean' },
    { key: 'keepalive', label: 'Keepalive (seconds)', kind: 'number' },
  ],
  custom: [],
};

const TYPE_DEFAULTS: Record<WorkflowCredential['credential_type'], Record<string, any>> = {
  postgres: { host: '', port: 5432, database: '', user: '', password: '', ssl_mode: 'prefer' },
  smtp: { host: '', port: 587, username: '', password: '', use_tls: true, from_email: '' },
  gmail: { access_token: '', refresh_token: '', email: '' },
  odoo: { base_url: '', db: '', username: '', password: '' },
  supabase: { base_url: '', api_key: '' },
  imap: { host: '', port: 993, username: '', password: '', use_ssl: true, mailbox: 'INBOX' },
  http: { base_url: '', auth_token: '', api_key: '' },
  shopify: { shop_domain: '', access_token: '', api_version: '2024-01' },
  hubspot: { access_token: '' },
  slack: { token: '' },
  pipedrive: { api_key: '' },
  microsoft365: { access_token: '', user_id: 'me' },
  asana: { access_token: '' },
  trello: { api_key: '', token: '' },
  netsuite: { account_id: '', consumer_key: '', consumer_secret: '', token_id: '', token_secret: '' },
  quickbooks: { access_token: '', realm_id: '' },
  sap: { base_url: '', username: '', password: '', access_token: '' },
  zoom: { account_id: '', client_id: '', client_secret: '' },
  jira: { base_url: '', email: '', api_token: '' },
  confluence: { base_url: '', email: '', api_token: '' },
  discord: { webhook_url: '', bot_token: '', channel_id: '' },
  microsoft_teams: { webhook_url: '' },
  google_calendar: { access_token: '', refresh_token: '', email: '' },
  google_sheets: { access_token: '', refresh_token: '', email: '' },
  google_drive: { access_token: '', refresh_token: '', email: '' },
  zoho_crm: { access_token: '', refresh_token: '', client_id: '', client_secret: '', region: 'com' },
  insightly: { api_key: '' },
  cal_com: { api_key: '', base_url: '' },
  erpnext: { base_url: '', api_key: '', api_secret: '' },
  notion: { api_token: '' },
  airtable: { access_token: '', base_id: '' },
  dropbox: { access_token: '' },
  mailchimp: { api_key: '', server_prefix: '' },
  xero: { access_token: '', tenant_id: '', refresh_token: '', client_id: '', client_secret: '' },
  myob: { access_token: '', api_key: '', company_file_id: '', cftoken: '', refresh_token: '', client_id: '', client_secret: '' },
  mqtt: { host: '', port: 1883, username: '', password: '', client_id: '', use_tls: false, keepalive: 60 },
  custom: {},
};

export const WorkflowCredentialsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [credentials, setCredentials] = useState<WorkflowCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WorkflowCredential | null>(null);
  const [name, setName] = useState('');
  const [credentialType, setCredentialType] = useState<WorkflowCredential['credential_type']>('postgres');
  const [formData, setFormData] = useState<Record<string, any>>({ ...TYPE_DEFAULTS.postgres });
  const [customPairs, setCustomPairs] = useState<Array<{ key: string; value: string }>>([{ key: '', value: '' }]);

  const [shopifyDialogOpen, setShopifyDialogOpen] = useState(false);
  const [shopifyCredName, setShopifyCredName] = useState('');
  const [shopifyDomain, setShopifyDomain] = useState('');
  const [shopifyConnecting, setShopifyConnecting] = useState(false);

  const [googleDialogOpen, setGoogleDialogOpen] = useState(false);
  const [googleCredName, setGoogleCredName] = useState('');
  const [googleLoginHint, setGoogleLoginHint] = useState('');
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [googleServiceType, setGoogleServiceType] = useState<
    'gmail' | 'google_sheets' | 'google_drive' | 'google_calendar'
  >('gmail');
  const [googleMenuAnchor, setGoogleMenuAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (searchParams.get('shopify_connected') === '1') {
      const shop = searchParams.get('shop') || '';
      enqueueSnackbar(`Shopify store "${shop}" connected successfully!`, { variant: 'success' });
      searchParams.delete('shopify_connected');
      searchParams.delete('shop');
      setSearchParams(searchParams, { replace: true });
      notifyCredentialsChanged(id);
    }
    if (searchParams.get('shopify_error')) {
      const err = searchParams.get('shopify_error') || 'unknown';
      enqueueSnackbar(`Shopify connection failed: ${err}`, { variant: 'error' });
      searchParams.delete('shopify_error');
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get('google_connected') === '1') {
      const email = searchParams.get('email') || '';
      enqueueSnackbar(
        email ? `Google account "${email}" connected successfully!` : 'Google account connected successfully!',
        { variant: 'success' }
      );
      searchParams.delete('google_connected');
      searchParams.delete('email');
      setSearchParams(searchParams, { replace: true });
      notifyCredentialsChanged(id);
    }
    if (searchParams.get('google_error')) {
      const err = searchParams.get('google_error') || 'unknown';
      enqueueSnackbar(`Google connection failed: ${err}`, { variant: 'error' });
      searchParams.delete('google_error');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openShopifyConnect = () => {
    setShopifyCredName('');
    setShopifyDomain('');
    setShopifyDialogOpen(true);
  };

  const openGoogleConnect = (
    serviceType: 'gmail' | 'google_sheets' | 'google_drive' | 'google_calendar' = 'gmail'
  ) => {
    setGoogleServiceType(serviceType);
    setGoogleCredName('');
    setGoogleLoginHint('');
    setGoogleDialogOpen(true);
  };

  const handleGoogleConnect = async () => {
    if (!googleCredName.trim()) {
      enqueueSnackbar('Please enter a credential name', { variant: 'warning' });
      return;
    }
    try {
      setGoogleConnecting(true);
      const resp = await apiService.get<{ authorize_url: string }>('/google/install/', {
        workflow_id: id,
        credential_name: googleCredName.trim(),
        credential_type: googleServiceType,
        ...(googleLoginHint.trim() ? { login_hint: googleLoginHint.trim() } : {}),
      });
      window.location.href = resp.authorize_url;
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.error || 'Failed to start Google connection',
        { variant: 'error' }
      );
      setGoogleConnecting(false);
    }
  };

  const GOOGLE_SERVICE_OPTIONS: Array<{
    value: 'gmail' | 'google_sheets' | 'google_drive' | 'google_calendar';
    label: string;
    description: string;
  }> = [
    { value: 'gmail', label: 'Gmail', description: 'Read, send & manage emails' },
    { value: 'google_sheets', label: 'Google Sheets', description: 'Read & write spreadsheets' },
    { value: 'google_drive', label: 'Google Drive', description: 'List, download & upload files' },
    { value: 'google_calendar', label: 'Google Calendar', description: 'Manage events & calendars' },
  ];

  const googleServiceLabel =
    GOOGLE_SERVICE_OPTIONS.find((opt) => opt.value === googleServiceType)?.label ?? 'Google';

  const handleShopifyConnect = async () => {
    if (!shopifyCredName.trim()) {
      enqueueSnackbar('Please enter a credential name', { variant: 'warning' });
      return;
    }
    if (!shopifyDomain.trim()) {
      enqueueSnackbar('Please enter your Shopify store domain', { variant: 'warning' });
      return;
    }
    try {
      setShopifyConnecting(true);
      const resp = await apiService.get<{ authorize_url: string; shop: string }>(
        '/shopify/install/',
        {
          shop: shopifyDomain.trim(),
          workflow_id: id,
          credential_name: shopifyCredName.trim(),
        }
      );
      window.location.href = resp.authorize_url;
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.error || 'Failed to start Shopify connection',
        { variant: 'error' }
      );
      setShopifyConnecting(false);
    }
  };

  const loadAll = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [wf, credsRaw] = await Promise.all([
        workflowService.getWorkflow(id),
        workflowService.getCredentials(id),
      ]);
      setWorkflow(wf);
      const rows: any = Array.isArray(credsRaw)
        ? credsRaw
        : Array.isArray((credsRaw as any)?.results)
          ? (credsRaw as any).results
          : [];
      setCredentials(rows);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load workflow credentials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setCredentialType('postgres');
    setFormData({ ...TYPE_DEFAULTS.postgres });
    setCustomPairs([{ key: '', value: '' }]);
    setDialogOpen(true);
  };

  const openEdit = (item: WorkflowCredential) => {
    setEditing(item);
    setName(item.name);
    setCredentialType(item.credential_type);
    const nextForm = { ...TYPE_DEFAULTS[item.credential_type], ...(item.data || {}) };
    // Back-compat: Odoo credentials used to store the secret under
    // `api_key`. Migrate it into `password` on edit so the user sees a
    // single, consistent field. The legacy key is dropped from the form,
    // but the executor still falls back to it at runtime if needed.
    if (item.credential_type === 'odoo') {
      if (!nextForm.password && nextForm.api_key) {
        nextForm.password = nextForm.api_key;
      }
      delete nextForm.api_key;
    }
    setFormData(nextForm);
    const pairs = Object.entries(item.data || {}).map(([k, v]) => ({ key: k, value: String(v ?? '') }));
    setCustomPairs(pairs.length > 0 ? pairs : [{ key: '', value: '' }]);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!id) return;
    if (!name.trim()) {
      enqueueSnackbar('Credential name is required', { variant: 'warning' });
      return;
    }
    const payloadData =
      credentialType === 'custom'
        ? customPairs.reduce((acc, row) => {
            const k = String(row.key || '').trim();
            if (!k) return acc;
            acc[k] = row.value;
            return acc;
          }, {} as Record<string, any>)
        : formData;
    try {
      setSaving(true);
      if (editing) {
        await workflowService.updateCredential(id, editing.id, {
          name: name.trim(),
          credential_type: credentialType,
          data: payloadData,
        } as any);
        enqueueSnackbar('Credential updated', { variant: 'success' });
      } else {
        await workflowService.createCredential(id, {
          name: name.trim(),
          credential_type: credentialType,
          data: payloadData,
          is_active: true,
        } as any);
        enqueueSnackbar('Credential created', { variant: 'success' });
      }
      setDialogOpen(false);
      await loadAll();
      notifyCredentialsChanged(id);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || 'Failed to save credential', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTypeChange = (nextType: WorkflowCredential['credential_type']) => {
    setCredentialType(nextType);
    setFormData({ ...TYPE_DEFAULTS[nextType] });
    setCustomPairs([{ key: '', value: '' }]);
  };

  const handleDelete = async (item: WorkflowCredential) => {
    if (!id) return;
    if (!window.confirm(`Delete credential "${item.name}"?`)) return;
    try {
      await workflowService.deleteCredential(id, item.id);
      enqueueSnackbar('Credential deleted', { variant: 'success' });
      await loadAll();
      notifyCredentialsChanged(id);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || 'Failed to delete credential', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate(id ? `/app/workflows/${id}` : '/app/workflows')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">
            Workflow Credentials{workflow?.name ? ` - ${workflow.name}` : ''}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GoogleIcon />}
            onClick={(e) => setGoogleMenuAnchor(e.currentTarget)}
          >
            Connect with Google
          </Button>
          <Menu
            anchorEl={googleMenuAnchor}
            open={Boolean(googleMenuAnchor)}
            onClose={() => setGoogleMenuAnchor(null)}
          >
            {GOOGLE_SERVICE_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                onClick={() => {
                  setGoogleMenuAnchor(null);
                  openGoogleConnect(opt.value);
                }}
              >
                <ListItemText primary={opt.label} secondary={opt.description} />
              </MenuItem>
            ))}
          </Menu>
          <Button
            variant="outlined"
            color="success"
            startIcon={<StorefrontIcon />}
            onClick={openShopifyConnect}
          >
            Connect with Shopify
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Credential
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {credentials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">No credentials yet.</TableCell>
              </TableRow>
            ) : (
              credentials.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.name}
                    {item.credential_type === 'shopify' && item.data?.shop_domain && (
                      <Chip
                        label="OAuth"
                        color="success"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {(['gmail', 'google_sheets', 'google_drive', 'google_calendar'] as const).includes(
                      item.credential_type as any
                    ) && item.data?.refresh_token && (
                      <Chip
                        label={item.data?.email ? `OAuth · ${item.data.email}` : 'OAuth'}
                        color="primary"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{item.credential_type}</TableCell>
                  <TableCell>{new Date(item.updated_at).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Credential' : 'Create Credential'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Credential Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="credential-type-label">Type</InputLabel>
            <Select
              labelId="credential-type-label"
              label="Type"
              value={credentialType}
              onChange={(e) => handleTypeChange(e.target.value as any)}
            >
              {CREDENTIAL_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {credentialType !== 'custom' ? (
            <Box>
              {FIELD_DEFS[credentialType].map((field) => {
                if (field.kind === 'boolean') {
                  return (
                    <FormControlLabel
                      key={field.key}
                      control={
                        <Switch
                          checked={Boolean(formData[field.key])}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, [field.key]: e.target.checked }))
                          }
                        />
                      }
                      label={field.label}
                      sx={{ mb: 1, display: 'block' }}
                    />
                  );
                }
                if (field.kind === 'select') {
                  return (
                    <FormControl key={field.key} fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
                      <Select
                        labelId={`${field.key}-label`}
                        label={field.label}
                        value={formData[field.key] ?? ''}
                        onChange={(e) => setFormData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        {(field.options || []).map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }
                return (
                  <TextField
                    key={field.key}
                    label={field.label}
                    type={field.kind === 'password' ? 'password' : field.kind === 'number' ? 'number' : 'text'}
                    value={formData[field.key] ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        [field.key]: field.kind === 'number' ? Number(raw || 0) : raw,
                      }));
                    }}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                  />
                );
              })}
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Add key-value pairs for custom credentials.
              </Typography>
              {customPairs.map((row, idx) => (
                <Box key={`pair-${idx}`} display="flex" gap={1} sx={{ mb: 1 }}>
                  <TextField
                    label="Key"
                    size="small"
                    fullWidth
                    value={row.key}
                    onChange={(e) => {
                      const next = [...customPairs];
                      next[idx] = { ...row, key: e.target.value };
                      setCustomPairs(next);
                    }}
                  />
                  <TextField
                    label="Value"
                    size="small"
                    fullWidth
                    value={row.value}
                    onChange={(e) => {
                      const next = [...customPairs];
                      next[idx] = { ...row, value: e.target.value };
                      setCustomPairs(next);
                    }}
                  />
                  <Button
                    color="error"
                    onClick={() => setCustomPairs((prev) => prev.filter((_, i) => i !== idx))}
                    disabled={customPairs.length <= 1}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              <Button variant="outlined" onClick={() => setCustomPairs((prev) => [...prev, { key: '', value: '' }])}>
                Add row
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shopify Connect Dialog */}
      <Dialog
        open={shopifyDialogOpen}
        onClose={() => !shopifyConnecting && setShopifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorefrontIcon color="success" />
          Connect with Shopify
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a name for this credential and your Shopify store domain. You'll be
            redirected to Shopify to authorize access, then brought back here automatically.
          </Typography>
          <TextField
            label="Credential Name"
            placeholder="e.g. My Shopify Store"
            value={shopifyCredName}
            onChange={(e) => setShopifyCredName(e.target.value)}
            fullWidth
            size="small"
            helperText="Display name for this credential inside ODA"
            disabled={shopifyConnecting}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            label="Shopify Store Domain"
            placeholder="mystore.myshopify.com"
            value={shopifyDomain}
            onChange={(e) => setShopifyDomain(e.target.value)}
            fullWidth
            size="small"
            helperText='Enter just the store name (e.g. "mystore") or full domain "mystore.myshopify.com"'
            disabled={shopifyConnecting}
            onKeyDown={(e) => e.key === 'Enter' && handleShopifyConnect()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShopifyDialogOpen(false)} disabled={shopifyConnecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleShopifyConnect}
            disabled={shopifyConnecting || !shopifyDomain.trim() || !shopifyCredName.trim()}
            startIcon={shopifyConnecting ? <CircularProgress size={16} color="inherit" /> : <StorefrontIcon />}
          >
            {shopifyConnecting ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Connect Dialog */}
      <Dialog
        open={googleDialogOpen}
        onClose={() => !googleConnecting && setGoogleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GoogleIcon color="primary" />
          Connect with Google ({googleServiceLabel})
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a name for this credential. You'll be redirected to Google to authorize{' '}
            <strong>{googleServiceLabel}</strong> access, then brought back here automatically.
            Access tokens are refreshed in the background &mdash; you won't need to paste anything
            manually.
          </Typography>
          <TextField
            label="Credential Name"
            placeholder={`e.g. My ${googleServiceLabel} Account`}
            value={googleCredName}
            onChange={(e) => setGoogleCredName(e.target.value)}
            fullWidth
            size="small"
            helperText="Display name for this credential inside ODA"
            disabled={googleConnecting}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            label="Account hint (optional)"
            placeholder="you@example.com"
            value={googleLoginHint}
            onChange={(e) => setGoogleLoginHint(e.target.value)}
            fullWidth
            size="small"
            helperText="Pre-fills which Google account to sign in with."
            disabled={googleConnecting}
            onKeyDown={(e) => e.key === 'Enter' && handleGoogleConnect()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoogleDialogOpen(false)} disabled={googleConnecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGoogleConnect}
            disabled={googleConnecting || !googleCredName.trim()}
            startIcon={googleConnecting ? <CircularProgress size={16} color="inherit" /> : <GoogleIcon />}
          >
            {googleConnecting ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

